import { useEffect, useState } from "react";
import type { FileDTO, FileGroup } from "../../shared/types";
import { FILE_VISIBILITIES } from "../../shared/types";
import { FileSharingBadge, fileUrl, formatBytes, iconFor } from "../components/Files";
import { Icon } from "../components/Icon";
import { MoreOnScroll, usePaged } from "../components/MoreOnScroll";
import { ShareDialog } from "../components/ShareDialog";
import { Table } from "../components/Table";
import { Tabs, useTabParam } from "../components/Tabs";
import { TextLink } from "../components/TextLink";
import { Button, ErrorText, inputCls, PageHeader, timeAgo } from "../components/ui";
import { get } from "../lib/api";
import { navigate, useLocation } from "../lib/router";

const GROUPS = ["mine", "shared", "everyone"] as const;
const GROUP_LABEL: Record<FileGroup, string> = { mine: "Mine", shared: "Shared with me", everyone: "Everyone" };
const GROUP_EMPTY: Record<FileGroup, string> = {
  mine: "No files yet. What you upload in a chat, and what agents make for you, lands here.",
  shared: "Nothing is shared with you yet.",
  everyone: "Nobody has shared a file with everyone yet.",
};

/**
 * A person's files (security plan D16): what they own — their uploads and what agents made working for them — what is
 * shared with them, and what is shared with everyone. Each row says whose it is and who else may open it; its owner
 * shares it from here, with the same control as a page.
 */
export function FilesPage() {
  const [group, setGroup] = useTabParam(GROUPS, "group");
  const { pathname, search } = useLocation();
  const q = search.get("q") ?? "";
  const [draft, setDraft] = useState(q);
  const [counts, setCounts] = useState<Record<FileGroup, number> | null>(null);
  const [sharing, setSharing] = useState<FileDTO | null>(null);

  // The search lives in the address, so a reload or a shared link keeps it.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (draft === q) return;
      const next = new URLSearchParams(location.search);
      if (draft.trim()) next.set("q", draft.trim());
      else next.delete("q");
      navigate(`${pathname}${next.size ? `?${next}` : ""}`, true);
    }, 300);
    return () => clearTimeout(timer);
  }, [draft]);

  const loadCounts = () => get<Record<FileGroup, number>>("/api/files?counts=1").then(setCounts, () => {});
  useEffect(() => void loadCounts(), []);
  const { rows, done, loading, error, more, reload } = usePaged<FileDTO>(
    (before) => get<FileDTO[]>(`/api/files?${new URLSearchParams({ group, ...(q ? { q } : {}), ...(before ? { before } : {}) })}`),
    [group, q],
  );

  return (
    <div>
      <PageHeader title="Files" subtitle="Yours, and what others share with you. A file is private to its owner until they share it." />
      <div className="space-y-4 px-4 py-4 md:px-6">
        <Tabs label="Files" value={group} onChange={setGroup} tabs={GROUPS.map((g) => ({ key: g, label: GROUP_LABEL[g], count: counts?.[g] }))} />
        <Table
          rows={rows ?? []}
          rowKey={(f) => f.id}
          filters={<input className={`${inputCls} max-w-sm`} placeholder="Search by name…" value={draft} onChange={(e) => setDraft(e.target.value)} />}
          empty={rows === null ? "Loading…" : q ? `No file named like “${q}”.` : GROUP_EMPTY[group]}
          columns={[
            {
              header: "Name",
              primary: true,
              cell: (f) => (
                <span className="flex min-w-0 items-center gap-2">
                  <Icon name={iconFor(f.mime)} className="size-3.5 shrink-0 text-faint" />
                  <span className="truncate font-medium">{f.name}</span>
                </span>
              ),
            },
            { header: "Owner", className: "text-muted", cell: (f) => (group === "mine" ? "You" : f.ownerName ?? "—") },
            { header: "Who can open it", cell: (f) => <FileSharingBadge file={f} /> },
            { header: "Size", className: "text-muted", cell: (f) => formatBytes(f.size) },
            { header: "When", className: "text-xs text-muted", cell: (f) => timeAgo(f.createdAt) },
            {
              header: "",
              align: "right",
              cell: (f) => (
                <span className="inline-flex items-center gap-3">
                  {f.canShare && <Button size="sm" onClick={() => setSharing(f)}>Share</Button>}
                  <TextLink href={fileUrl(f.id)} className="text-xs">Download</TextLink>
                </span>
              ),
            },
          ]}
        />
        {error && <ErrorText error={error} />}
        <MoreOnScroll onMore={more} done={done} loading={loading} />
      </div>
      {sharing && (
        <ShareDialog
          url={`/api/files/${sharing.id}/sharing`}
          title={`Share ${sharing.name}`}
          visibilities={FILE_VISIBILITIES}
          open
          onClose={() => setSharing(null)}
          onSaved={() => {
            setSharing(null);
            reload();
            void loadCounts();
          }}
        />
      )}
    </div>
  );
}
